import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { logoutUser } from '../services/auth';

const UserIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ShieldIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const AccountPage = ({ user }) => {
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const handleSignOut = () => {
    logoutUser();
    window.location.reload();
  };

  return (
    <div className="flex min-h-screen bg-gray-900 text-gray-200 font-sans">
      <Sidebar user={user} onSignOut={handleSignOut} />
      <div className="flex-1 p-8">
        <header className="mb-8">
          <h2 className="text-3xl font-bold text-white flex items-center">
            <UserIcon className="mr-3 text-blue-400" />
            Account Settings
          </h2>
          <p className="text-gray-400 mt-2">Manage your account information and preferences</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Account Information */}
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <UserIcon className="mr-2 text-gray-400" />
              Account Information
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Email Address</span>
                <span className="text-white font-medium">{user.email}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Account Role</span>
                <span className="text-white font-medium capitalize">{user.role}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-400">Account Status</span>
                <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                  Active
                </span>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
              <ShieldIcon className="mr-2 text-gray-400" />
              Security
            </h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-700 rounded-lg">
                <h4 className="text-white font-medium mb-2">Password</h4>
                <p className="text-gray-400 text-sm mb-3">
                  Your password is securely hashed and stored. Contact an administrator to change it.
                </p>
                <button 
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors duration-200"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                >
                  Request Password Change
                </button>
              </div>
              
              {showPasswordForm && (
                <div className="p-4 bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg">
                  <p className="text-blue-200 text-sm">
                    Password changes must be requested through an administrator. 
                    Please contact your system administrator to update your password.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* System Information */}
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 lg:col-span-2">
            <h3 className="text-xl font-semibold text-white mb-6">System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-blue-400 mb-1">
                  {user.cameras ? user.cameras.length : 0}
                </div>
                <div className="text-gray-400 text-sm">Active Cameras</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-green-400 mb-1">
                  {user.alerts ? user.alerts.length : 0}
                </div>
                <div className="text-gray-400 text-sm">Total Alerts</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400 mb-1">
                  24/7
                </div>
                <div className="text-gray-400 text-sm">Monitoring</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;